import { mapInventorizationSyncPayloadToSubmitData } from '@/features/sync/engine/inventorization-submit-data';
import type {
  InventorizationSyncPayload,
  InventorizationSyncPhotoRef,
} from '@/features/sync/engine/inventorization-sync-payload';
import { PHOTO_CONTEXTS } from '@/shared/capture/capture-session';
import type { FloorplanBlobRecord, PhotoBlobRecord } from '@/shared/db/schema';

export interface PhotoBlobReader {
  get(id: string): Promise<PhotoBlobRecord | undefined>;
}

export interface FloorplanBlobReader {
  getById(id: string): Promise<FloorplanBlobRecord | undefined>;
}

export async function createInventorizationMultipartPayload(input: {
  floorplanBlobs: FloorplanBlobReader;
  payload: InventorizationSyncPayload;
  photoBlobs: PhotoBlobReader;
}) {
  const formData = new FormData();
  let dataPayload: Record<string, unknown> = { ...mapInventorizationSyncPayloadToSubmitData(input.payload) };

  if (input.payload.pendingFloorplan?.localBlobId) {
    const floorplanRecord = await input.floorplanBlobs.getById(input.payload.pendingFloorplan.localBlobId);

    if (!floorplanRecord) {
      throw new Error(`Floorplan blob ${input.payload.pendingFloorplan.localBlobId} was not found.`);
    }

    if (floorplanRecord.pendingUpload) {
      dataPayload = {
        ...dataPayload,
        pendingFloorplan: {
          buildingFloor: floorplanRecord.buildingFloor,
          buildingId: floorplanRecord.buildingId,
          floorplanId: floorplanRecord.floorplanId,
          localBlobId: floorplanRecord.id,
          locationId: floorplanRecord.locationId,
          mimeType: floorplanRecord.mimeType,
          name: floorplanRecord.name,
        },
      };

      formData.append('files.floorplan', floorplanRecord.blob, createFloorplanFileName(floorplanRecord));
    } else {
      dataPayload = {
        ...dataPayload,
        pendingFloorplan: undefined,
      };
    }
  }

  formData.append('data', JSON.stringify(dataPayload));

  const photos =
    input.payload._id || input.payload.id
      ? input.payload.photos.filter(photo => !photo.uploadedRemoteId)
      : input.payload.photos;

  for (const [index, photo] of photos.entries()) {
    const blobRecord = await input.photoBlobs.get(photo.localBlobId);

    if (!blobRecord) {
      throw new Error(`Photo blob ${photo.localBlobId} was not found.`);
    }

    formData.append(
      createPhotoFormFieldName(photo, input.payload, index),
      blobRecord.blob,
      createPhotoFileName(photo, blobRecord),
    );
  }

  return formData;
}

function createPhotoFormFieldName(
  photo: InventorizationSyncPhotoRef,
  payload: InventorizationSyncPayload,
  fallbackIndex: number,
) {
  const contextIndex = payload.photos
    .filter(candidate => candidate.context === photo.context)
    .findIndex(candidate => candidate.id === photo.id);

  if (photo.context === PHOTO_CONTEXTS.context) {
    return `files.contextList.${Math.max(contextIndex, 0)}`;
  }

  if (photo.context === PHOTO_CONTEXTS.detail) {
    return `files.detailList.${Math.max(contextIndex, 0)}`;
  }

  if (photo.context === PHOTO_CONTEXTS.module) {
    return `files.moduleList.${Math.max(contextIndex, 0)}`;
  }

  if (photo.context === PHOTO_CONTEXTS.register) {
    const registerIndex = Math.max(
      payload.readings.registers.findIndex(register => register.id === photo.registerId),
      0,
    );
    const registerPhotoIndex = payload.photos
      .filter(candidate => candidate.context === PHOTO_CONTEXTS.register && candidate.registerId === photo.registerId)
      .findIndex(candidate => candidate.id === photo.id);

    return `files.register.${registerIndex}.List.${Math.max(registerPhotoIndex, 0)}`;
  }

  return `files.unknown.${fallbackIndex}`;
}

function createPhotoFileName(photo: InventorizationSyncPhotoRef, blobRecord: PhotoBlobRecord) {
  const extension = readImageExtension(blobRecord.mimeType);

  return `${photo.id}${extension}`;
}

function createFloorplanFileName(blobRecord: FloorplanBlobRecord) {
  if (blobRecord.name?.trim()) {
    return blobRecord.name;
  }

  return `${blobRecord.id}${readImageExtension(blobRecord.mimeType ?? 'image/jpeg')}`;
}

function readImageExtension(mimeType: string) {
  if (mimeType === 'image/png') {
    return '.png';
  }

  if (mimeType === 'image/webp') {
    return '.webp';
  }

  return '.jpg';
}
